import { useTranslation } from 'react-i18next';
import { RepositoryContext } from '../../store/RepositoryProvider';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import API from '../../server/api';
import NavBar from '../../components/NavBar';
import Price, { CURRENCY } from '../../components/Price';
import InfoDescription from '../../components/InfoDescription';
import Table from '../../components/Table';
import CashboxDetails from '../../models/cashbox/CashboxDetails';
import Button, { BUTTON_POSITION } from '../../components/Button';
import { CollectionType } from '../../models/types';
import Modal from '../../components/Modal';
import HandCash from './hand_cash';
import TakeSalary from './take_salary';
import DateFormat from '../../components/DateFormat';
import log from '../../utils/logger';
import Badge, {BADGE_TYPE} from "../../components/Badge";
import Service from '../../utils/service';
import DateTZ from '../../models/date/DateTZ';
import DayPicker from '../../components/DayPicker';
import DatePeriod from '../../components/DatePeriod';
import CancelSalary from './cancel_salary';


const Cashbox = () => {
  const {t} = useTranslation(['common', 'cashbox']);

  const repository = useContext(RepositoryContext);

  const [startDate, setStartDate] = useState(DateTZ.startOf('month'));
  const [endDate, setEndDate] = useState(DateTZ.endOf('month'));
  const [range, setRange] = useState(null);

  const [cashbox, setCashbox] = useState(new CashboxDetails());
  const [lastSalary, setLastSalary] = useState(null);

  const collections = useRef([]);
  const newCollections = useRef([]);
  const statusService = useRef(null);
  const startDateRef = useRef(startDate);
  const endDateRef = useRef(endDate);
  
  const [showCashModal, setShowCashModal] = useState(false);
  const handleCloseCashModal = () => setShowCashModal(false);
  
  const [showSalaryModal, setShowSalaryModal] = useState(false);
  const handleCloseSalaryModal = () => setShowSalaryModal(false);
  
  const [showCancelSalaryModal, setShowCancelSalaryModal] = useState(false);
  const handleCloseCancelSalaryModal = () => setShowCancelSalaryModal(false);

  const handleApplyRangeModal = useCallback((range) => {
    setStartDate(new DateTZ(range.from).dayjs.startOf('day'));
    setEndDate(new DateTZ(range.to || range.from).dayjs.endOf('day'));
  }, []);

  const loadCashbox = async (callback) => {
    repository.action(API.cashier.cashbox, {
      fromDate: DateFormat.Request({ date: startDateRef.current }),
      toDate: DateFormat.Request({ date: endDateRef.current })
    }).then((cashbox) => {
      setCashbox(cashbox);
      callback && callback();
    }).catch((error) => {
      log.error(error);
      setCashbox(new CashboxDetails());
    });

    getLastSalary();
  };

  const updateHistory = () => {
    repository.action(API.cashier.cashbox, {
      fromDate: DateFormat.Request({ date: startDateRef.current }),
      toDate: DateFormat.Request({ date: endDateRef.current })
    }).then((cashbox) => {
      let history = cashbox.history.filter((h) => !collections.current.find((c) => h.day === c.day && h.time === c.time) || newCollections.current.find((c) => h.day === c.day && h.time === c.time));
      setCashbox(cashbox);
      newCollections.current = history;
      collections.current = cashbox.history;
    }).catch((e) => {
      log.error(e);
      collections.current = [];
    });
  };

  const getLastSalary = async () => {
    const now = new DateTZ();
    const redoDay = DateTZ.subtract(1, 'day');
    repository.action(API.cashier.cashbox, {
      fromDate: DateFormat.Request({ date: redoDay }),
      toDate: DateFormat.Request({ date: now })
    }).then((cashbox) => {
      let salaries = cashbox.history.filter((salary) => salary.type === CollectionType.SALARY && redoDay.isBefore(salary.date));
      setLastSalary(salaries.length > 0 ? salaries[0] : null);
    }).catch((error) => {
      log.error(error);
      setLastSalary(null);
    });
  };


  useEffect(() => {
    startDateRef.current = startDate;
    endDateRef.current = endDate;

    setRange({ from: startDate, to: endDate });
    loadCashbox();
  }, [startDate, endDate]);

  useEffect(() => {
    let service = new Service(() => {
      updateHistory();
    }, 20000);

    loadCashbox(() => {
      collections.current = cashbox.history;
      service.start(20000);
      statusService.current = service;
    });

    return () => {
      statusService.current && statusService.current.stop();
      service.stop();
    };
  }, []);

  return (
    <div className="trip-app">
      <NavBar title={t('cashbox:cashbox.title')} />
      <div className="trip-app-body">
        <table className="info-table">
          <tbody>
          <tr>
            <td>
              <InfoDescription description={t('cashbox:total.total_cash')}>
                <Price currency={CURRENCY.RUB} value={cashbox.total.totalCash-cashbox.total.totalRefundCash} />
              </InfoDescription>
              <Button.Panel className="mb-0 mt-0 pt-3">
                <Button position={BUTTON_POSITION.LEFT} onClick={() => setShowCashModal(true)}>{t('cashbox:button.hand_cash')}</Button>
              </Button.Panel>
            </td>
            <td>
              <InfoDescription description={t('cashbox:total.total_salary')}>
                <Price currency={CURRENCY.RUB} value={cashbox.total.totalSalary} />
              </InfoDescription>
              <Button.Panel className="mb-0 mt-0 pt-3" gap={4}>
                <Button position={BUTTON_POSITION.LEFT} onClick={() => setShowSalaryModal(true)}>
                    {t('cashbox:button.take_salary')}
                </Button>
                {lastSalary && (
                  <Button position={BUTTON_POSITION.LEFT} onClick={() => setShowCancelSalaryModal(true)}>
                      {t('cashbox:button.cancel_salary')}
                  </Button>
                )}
              </Button.Panel>
            </td>
          </tr>
          </tbody>
        </table>

        <div className="header-line mb-3">
          <span>{t('cashbox:cashbox.total')}</span>
          <DayPicker.Input className="link-action" selected={range} onChange={handleApplyRangeModal}>
            <DatePeriod start={startDate} end={endDate} />
          </DayPicker.Input>
        </div>

        <table className="info-table">
          <tbody>
          <tr>
            <td>
              <InfoDescription description={t('cashbox:stat.total_cash')}>
                <Price currency={CURRENCY.RUB} value={cashbox.stat.totalAmount} />
              </InfoDescription>
            </td>
            <td>
              <InfoDescription description={t('cashbox:stat.total_other_amount')}>
                <Price currency={CURRENCY.RUB} value={cashbox.stat.totalOtherAmount} />
              </InfoDescription>
            </td>
            <td>
              <InfoDescription description={t('cashbox:stat.total_amount')}>
                <Price currency={CURRENCY.RUB} value={cashbox.stat.totalAmount} />
              </InfoDescription>
            </td>
          </tr>
          <tr>
            <td className="delim" colSpan={3}></td>
          </tr>
          <tr>
            <td>
              <InfoDescription description={t('cashbox:stat.total_refund_cash')}>
                <Price currency={CURRENCY.RUB} value={cashbox.stat.totalRefundCash} />
              </InfoDescription>
            </td>
            <td>
              <InfoDescription description={t('cashbox:stat.total_other_refunds')}>
                <Price currency={CURRENCY.RUB} value={cashbox.stat.totalOtherRefunds} />
              </InfoDescription>
            </td>
            <td>
              <InfoDescription description={t('cashbox:stat.total_refunds')}>
                <Price currency={CURRENCY.RUB} value={cashbox.stat.totalRefunds} />
              </InfoDescription>
            </td>
          </tr>
          <tr>
            <td className="delim" colSpan={3}></td>
          </tr>
          <tr>
            <td>
              <InfoDescription description={t('cashbox:stat.collected_amount')}>
                <Price currency={CURRENCY.RUB} value={cashbox.stat.collectedAmount} />
              </InfoDescription>
            </td>
            <td>
              <InfoDescription description={t('cashbox:stat.salary_withdrawn')}>
                <Price currency={CURRENCY.RUB} value={cashbox.stat.salaryWithdrawn} />
              </InfoDescription>
            </td>
            <td>
              <InfoDescription description={t('cashbox:stat.total_orders')}>
                <Price currency={CURRENCY.COUNT} value={cashbox.stat.totalOrders} />
              </InfoDescription>
          </td>
          </tr>
        </tbody>
      </table>

      <div className="header-line mb-2 mt-2">
          {t('cashbox:cashbox.history')}
      </div>

        <Table cols={3}>
          {cashbox.history.map((h, i) => (
            <Table.Row key={i} delim={true} className={`pb-2 ${newCollections.current.find((c) => h.day === c.day && h.time === c.time) ? 'active' : ''}`}>
              <Table.Cell colspan={2}>
                {h.type === CollectionType.COLLECTION ?
                  <InfoDescription description={t('cashbox:history.take', { name: h?.collector?.profile?.fullName || '-'})}>
                    {t(`cashbox:history.${h.type}`)}
                    <DateFormat.DateTime date={h.date} tz={true} />
                  </InfoDescription>
                  :
                  <InfoDescription>
                    {t(`cashbox:history.${h.type}`)}
                    <DateFormat.DateTime date={h.date} tz={true} />
                  </InfoDescription>
                }
              </Table.Cell>
              <Table.Cell className="align-right">
                <Badge type={BADGE_TYPE.INFO}>
                  <Price currency={CURRENCY.RUB} value={h.amount*-1} />
                </Badge>
              </Table.Cell>
            </Table.Row>
          ))}
        </Table>

        <Modal show={showCashModal} onHide={handleCloseCashModal}>
          <Modal.Header closeButton>
            {t('cashbox:total_cash.title')}
          </Modal.Header>
          <Modal.Body>
            {handleCloseCashModal && <HandCash onClose={handleCloseCashModal} amountMax={cashbox.total.totalCash-cashbox.total.totalRefundCash} />}
          </Modal.Body>
        </Modal>

        <Modal show={showSalaryModal} onHide={handleCloseSalaryModal}>
          <Modal.Header closeButton>
            {t('cashbox:total_salary.title')}
          </Modal.Header>
          <Modal.Body>
            <TakeSalary amountMax={cashbox.total.totalSalary} onApply={() => loadCashbox()} onCancel={handleCloseSalaryModal}/>
          </Modal.Body>
        </Modal>

        <Modal show={showCancelSalaryModal} onHide={handleCloseCancelSalaryModal}>
          <Modal.Header closeButton>
            {t('cashbox:cancel_salary.title')}
          </Modal.Header>
          <Modal.Body>
            <CancelSalary salaryId={lastSalary?.id} amount={lastSalary?.amount} onApply={() => loadCashbox()} onCancel={handleCloseCancelSalaryModal}/>
          </Modal.Body>
        </Modal>
      </div>
    </div>
  )
}

export default Cashbox;